import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AuthContext, API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const MyCopiesPage = () => {
  const { user, token } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/my-listings`, { headers });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load copies");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this copy from your listings?")) return;
    try {
      await axios.delete(`${API}/copies/${id}`, { headers });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Copy removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => {
    if (user && token) load();
  }, [user, token]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <Navbar />
      <div data-testid="my-copies-page" className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold" style={{ color: "var(--primary)" }}>
          My Copies
        </h1>
        <p className="mt-2 text-gray-600">
          Books you own and list for others to rent. This is not your borrowing history — see My Rentals for books you have borrowed, and Transactions for money movements.
        </p>

        <div className="mt-8">
          {loading && (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          )}
          {!loading && items.length === 0 && (
            <div
              className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500"
              data-testid="my-copies-empty"
            >
              You haven't listed any copies yet. Add books from your dashboard.
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2 font-medium">Book</th>
                    <th className="px-3 py-2 font-medium">Category</th>
                    <th className="px-3 py-2 font-medium">Pincode</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Times rented</th>
                    <th className="px-3 py-2 font-medium">Earnings</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-t" data-testid={`my-copy-${c.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium" data-testid="copy-title">{c.title || "Untitled book"}</div>
                        {c.author && <div className="text-xs text-gray-500">by {c.author}</div>}
                      </td>
                      <td className="px-3 py-2 capitalize">{c.category || "—"}</td>
                      <td className="px-3 py-2">{c.city || "—"} {c.pincode ? `· ${c.pincode}` : ""}</td>
                      <td className="px-3 py-2">
                        <Badge variant={c.status === "available" ? "default" : "secondary"} className="capitalize">
                          {c.status || c.condition || "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{c.times_rented ?? 0}</td>
                      <td className="px-3 py-2">₹{Math.round(c.earnings || 0)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {c.book_id && (
                          <Link to={`/catalog/${c.book_id}`} className="underline mr-3" style={{ color: "var(--primary)" }}>
                            View
                          </Link>
                        )}
                        <button type="button" onClick={() => remove(c.id)} className="text-red-600" data-testid={`remove-copy-${c.id}`}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MyCopiesPage;
